import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, LogIn } from "lucide-react";

const LOGO_SRC = "/brand/site-logo.png";

function CbLogo({ size = 36 }) {
  return (
    <img
      src={LOGO_SRC}
      alt="ClippingBase"
      className="block shrink-0 object-contain"
      style={{ width: size, height: size }}
    />
  );
}

export function MarketingNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const onCreator = location.pathname.startsWith("/creator");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const loginHref = "/login";

  return (
    <>
      <header
        data-testid="cb-navbar"
        className="sticky top-0 z-50 w-full bg-transparent"
        style={{ backgroundColor: "transparent" }}
      >
        <div className="mx-auto flex w-full max-w-7xl items-center justify-center px-4 sm:px-6">
          <div
            className={`flex w-full items-center justify-between transition-all duration-300 ease-out ${
              scrolled
                ? "mx-auto mt-3 h-14 max-w-3xl rounded-2xl border border-white/10 bg-white/[0.06] px-4 backdrop-blur-2xl shadow-[0_10px_40px_rgba(0,0,0,0.45)]"
                : "h-16 max-w-7xl rounded-2xl border border-white/[0.05] bg-white/[0.02] px-4 backdrop-blur-md sm:px-5"
            }`}
          >
            {/* Logo */}
            <Link to="/" data-testid="cb-nav-logo" className="flex items-center gap-2.5">
              <CbLogo size={40} />
              <span className="cb-display text-[19px] tracking-tight text-white">
                ClippingBase<span className="text-[#5a5a60]">.com</span>
              </span>
            </Link>

            {/* Right: Contact + CTA */}
            <div className="flex items-center gap-3">
              <Link
                to="/contact"
                data-testid="cb-nav-contact"
                className="hidden rounded-full px-3 py-2 text-sm text-zinc-300 transition-colors hover:text-white sm:inline-flex"
              >
                Contact
              </Link>

              {onCreator ? (
                <a
                  href={loginHref}
                  data-testid="cb-nav-login-btn"
                  className="cb-btn-hover hidden items-center gap-1.5 rounded-full bg-[#d8ff2b] px-4 py-2 text-sm font-bold text-black hover:bg-[#c5ee1a] sm:inline-flex"
                >
                  <LogIn size={15} strokeWidth={2.5} />
                  Login
                </a>
              ) : (
                <>
                  <a
                    href={loginHref}
                    data-testid="cb-nav-login-btn"
                    className="hidden items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.04] px-4 py-2 text-sm font-semibold text-white transition-colors hover:border-[#d8ff2b]/50 hover:bg-white/[0.08] sm:inline-flex"
                  >
                    <LogIn size={15} strokeWidth={2.25} />
                    Login
                  </a>
                  <Link
                    to="/creator"
                    data-testid="cb-nav-creator-cta"
                    className="cb-btn-hover hidden rounded-full bg-[#d8ff2b] px-4 py-2 text-sm font-bold text-black hover:bg-[#c5ee1a] sm:inline-flex"
                  >
                    I&apos;m a Creator
                  </Link>
                </>
              )}

              {/* Mobile toggle */}
              <button
                onClick={() => setMobileOpen((v) => !v)}
                data-testid="cb-nav-mobile-toggle"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-zinc-300 transition-colors hover:border-[#d8ff2b]/40 hover:text-white md:hidden"
                aria-label="Toggle navigation"
              >
                {mobileOpen ? <X size={18} /> : <Menu size={18} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile drawer — overlay only, does NOT push the page down */}
        {mobileOpen && (
          <div
            data-testid="cb-nav-mobile-drawer"
            className="absolute left-4 right-4 top-[68px] z-[60] rounded-2xl border border-white/10 bg-[#0a0a0c]/95 backdrop-blur-2xl shadow-[0_20px_60px_rgba(0,0,0,0.55)] md:hidden"
          >
            <div className="flex flex-col gap-1 px-3 py-3">
              <Link
                to="/contact"
                data-testid="cb-nav-mobile-contact"
                className="rounded-xl px-3 py-3 text-sm text-zinc-200 hover:bg-white/5"
              >
                Contact
              </Link>
              <a
                href={loginHref}
                data-testid="cb-nav-mobile-login-btn"
                className="flex items-center gap-2 rounded-xl px-3 py-3 text-left text-sm font-semibold text-white hover:bg-white/5"
              >
                <LogIn size={15} strokeWidth={2.25} />
                Login
              </a>
              {!onCreator && (
                <Link
                  to="/creator"
                  data-testid="cb-nav-mobile-creator-cta"
                  className="mt-1 rounded-xl bg-[#d8ff2b] px-3 py-3 text-center text-sm font-bold text-black"
                >
                  I'm a Creator
                </Link>
              )}
            </div>
          </div>
        )}
      </header>
    </>
  );
}
